import { intlFormat } from 'date-fns/intlFormat';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useGetAccountingWithSlotsQuery } from '@/hooks/queries/use-get-accounting-with-slots';
import { useGetMeQuery } from '@/hooks/queries/use-me';
import { formatPeriodDays } from '@/utils/dates/format-period-days';
import { doctorFullnameTitleTrad } from '@/utils/doctor';

export const useAccoutingRetrocessionFeeViewModel = ({
  accountingId,
}: {
  accountingId?: string;
}) => {
  const params = useParams() as { accountingId: string };
  const [logoLoaded, setLogoLoaded] = useState<boolean>(false);

  // Guard
  const { data: me, isPending: mePending } = useGetMeQuery();
  const { data: accounting, isPending: accoutingPending } =
    useGetAccountingWithSlotsQuery(
      {
        id: accountingId ?? params.accountingId,
      },
      !mePending,
    );

  const pageLoaded = useMemo(() => {
    const meLoaded = !!me && !mePending;
    const accountingLoaded = !!accounting && !accoutingPending;

    return meLoaded && accountingLoaded;
  }, [mePending, accounting, accoutingPending]);

  return {
    unauthorized: !mePending && !me,
    pageLoaded,
    logoLoaded,
    setLogoLoaded,
    information: accounting
      ? {
          date: intlFormat(
            new Date(),
            {
              month: 'long',
              day: '2-digit',
              year: 'numeric',
            },
            {
              locale: 'fr',
            },
          ),
          doctorAttending: {
            ...accounting.doctorAttending,
            fullname: doctorFullnameTitleTrad(accounting.doctorAttending),
          },
          doctorSubstitute: {
            ...accounting.doctorSubstitute,
            fullname: doctorFullnameTitleTrad(accounting.doctorSubstitute),
          },
          period: formatPeriodDays(accounting.accountPeriod),
          amountDue: accounting.amountDue,
          amountPaid: accounting.amountPaid,
          amountPaidRetrocessionFee: accounting.amountPaidRetrocessionFee,
          amountMedicalAssistantFee: accounting.amountMedicalAssistantFee,
          amountPayBack: accounting.amountPayBack,
          medicalAssistantFee: accounting.medicalAssistantFee,
          agendaSlots: accounting.agendaSlots,
        }
      : null,
  };
};
export type AccoutingRetrocessionFeeViewModel = ReturnType<
  typeof useAccoutingRetrocessionFeeViewModel
>;
